import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, inputStyle, btnPrimary, FormRow } from './InventoryShared';

const PREFIX_FIELDS = [
  ['product_prefix', 'product_next_number', 'product_number_length', 'Product'],
  ['movement_prefix', 'movement_next_number', 'movement_number_length', 'Movement'],
  ['count_prefix', 'count_next_number', 'count_number_length', 'Count'],
  ['adjustment_prefix', 'adjustment_next_number', 'adjustment_number_length', 'Adjustment'],
  ['picking_prefix', 'picking_next_number', 'picking_number_length', 'Picking'],
  ['return_prefix', 'return_next_number', 'return_number_length', 'Return'],
  ['transfer_prefix', 'transfer_next_number', 'transfer_number_length', 'Transfer'],
];

const TEXT_RULE_FIELDS = [
  ['sku_requirement', 'SKU Requirement'],
  ['barcode_requirement', 'Barcode Requirement'],
  ['negative_stock_rule', 'Negative Stock Rule'],
  ['costing_method', 'Costing Method'],
  ['insufficient_stock_behavior', 'Insufficient Stock Behavior'],
  ['reservation_priority', 'Reservation Priority'],
  ['picking_strategy', 'Picking Strategy'],
  ['over_picking_rule', 'Over-Picking Rule'],
  ['count_movement_handling', 'Count Movement Handling'],
  ['return_inspection_rule', 'Return Inspection Rule'],
  ['internal_barcode_type', 'Internal Barcode Type'],
];

const BOOL_FIELDS = [
  ['allow_split_across_warehouses', 'Allow Split Across Warehouses'],
  ['allow_partial_picking', 'Allow Partial Picking'],
  ['blind_count_enabled', 'Blind Count Enabled'],
];

const NOTIFY_FIELDS = [
  ['notify_low_stock', 'Low Stock'],
  ['notify_out_of_stock', 'Out of Stock'],
  ['notify_reservation_shortage', 'Reservation Shortage'],
  ['notify_transfer_overdue', 'Transfer Overdue'],
  ['notify_count_variance', 'Count Variance'],
  ['notify_adjustment_approval', 'Adjustment Approval'],
  ['notify_picking_ready', 'Picking Ready'],
  ['notify_return_received', 'Return Received'],
  ['notify_batch_expiry', 'Batch Expiry'],
];

const JSON_FIELDS = [
  ['adjustment_approval_rules', 'Adjustment Approval Rules'],
  ['recount_rules', 'Recount Rules'],
  ['adjustment_reasons', 'Adjustment Reasons'],
];

export default function InventorySettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [form, setForm] = useState({});
  const [jsonText, setJsonText] = useState({});
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/inventory/settings`, { headers: getAuthHeaders() });
      const json = await res.json();
      setForm(json);
      const jt = {};
      JSON_FIELDS.forEach(([f]) => { jt[f] = JSON.stringify(json[f] ?? {}, null, 2); });
      setJsonText(jt);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const u = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const submit = async () => {
    setSaving(true); setMsg('');
    try {
      const body = { ...form };
      for (const [f] of JSON_FIELDS) {
        try { body[f] = JSON.parse(jsonText[f]); } catch { setMsg(`Invalid JSON in ${f}`); setSaving(false); return; }
      }
      const res = await fetch(`${API}/companies/${companyId}/inventory/settings`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to save');
      setMsg('Settings saved successfully.');
      fetchSettings();
    } catch (e) { setMsg(e.message); }
    setSaving(false);
  };

  if (initialLoading) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>⚙️ Inventory Settings</h2>
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </div>
      {msg && <div style={{ marginBottom: 16, fontSize: 13, color: msg.includes('success') ? '#10b981' : '#ef4444' }}>{msg}</div>}

      <Section title="Numbering">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {PREFIX_FIELDS.map(([pf, nf, lf, label]) => (
            <React.Fragment key={pf}>
              <FormRow label={`${label} Prefix`}><input value={form[pf] || ''} onChange={e => u(pf, e.target.value)} style={inputStyle} /></FormRow>
              <FormRow label={`${label} Next Number`}><input type="number" value={form[nf] ?? ''} onChange={e => u(nf, e.target.value)} style={inputStyle} /></FormRow>
              <FormRow label={`${label} Number Length`}><input type="number" value={form[lf] ?? ''} onChange={e => u(lf, e.target.value)} style={inputStyle} /></FormRow>
            </React.Fragment>
          ))}
        </div>
      </Section>

      <Section title="Business Rules">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {TEXT_RULE_FIELDS.map(([f, label]) => (
            <FormRow key={f} label={label}><input value={form[f] || ''} onChange={e => u(f, e.target.value)} style={inputStyle} /></FormRow>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 8 }}>
          {BOOL_FIELDS.map(([f, label]) => (
            <label key={f} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!form[f]} onChange={e => u(f, e.target.checked)} /> {label}
            </label>
          ))}
        </div>
      </Section>

      <Section title="Notifications">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {NOTIFY_FIELDS.map(([f, label]) => (
            <label key={f} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!form[f]} onChange={e => u(f, e.target.checked)} /> {label}
            </label>
          ))}
        </div>
      </Section>

      <Section title="Advanced Configuration (JSON)">
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Advanced configuration for module behavior. Edit as JSON.</div>
        {JSON_FIELDS.map(([f, label]) => (
          <FormRow key={f} label={label}>
            <textarea style={{ ...inputStyle, minHeight: 80, fontFamily: 'monospace', fontSize: 12 }}
              value={jsonText[f] ?? '{}'} onChange={e => setJsonText({ ...jsonText, [f]: e.target.value })} />
          </FormRow>
        ))}
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 40 }}>
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginTop: 0, marginBottom: 14 }}>{title}</h3>
      {children}
    </div>
  );
}
